import theme from '@styles/theme'
import React from 'react'
import { FaCheckCircle, FaCircle, FaExclamationCircle } from 'react-icons/fa'
import {
  Step,
  StepContact,
  StepContent,
  StepDescription,
  StepIconContainer,
  StepTitle
} from './StepBlock.styled'

export type StepStatus = 'blocked' | 'complete' | 'current' | 'warning'

interface Props {
  status?: StepStatus
  isBlocked?: boolean
  isCompleted?: boolean
  title?: React.ReactNode
  descBlocked?: React.ReactElement | string
  descCurrent?: React.ReactElement | string
  descComplete?: React.ReactElement | string
  isLast?: boolean
}

const STEP_ICON = {
  blocked: FaCircle,
  complete: FaCheckCircle,
  current: FaCircle,
  warning: FaExclamationCircle
} as const

const STEP_COLOR = {
  blocked: theme.colors.neutralGray,
  complete: theme.colors.primary,
  current: theme.colors.primary,
  warning: theme.colors.warning
} as const

const resolveStepStatus = ({
  status,
  isBlocked,
  isCompleted
}: Pick<Props, 'status' | 'isBlocked' | 'isCompleted'>): StepStatus => {
  if (status) return status
  if (isBlocked) return 'blocked'
  if (isCompleted) return 'complete'
  return 'current'
}

export const StepBlock: React.FC<Props> = ({
  status,
  isBlocked,
  isCompleted,
  title,
  descBlocked,
  descCurrent,
  descComplete,
  isLast = false
}) => {
  const resolved = resolveStepStatus({ status, isBlocked, isCompleted })
  const Icon = STEP_ICON[resolved]
  const statusColor = STEP_COLOR[resolved]
  const description =
    resolved === 'blocked' ? descBlocked : resolved === 'complete' ? descComplete : descCurrent
  const connectorColor = resolved === 'complete' ? theme.colors.primary : theme.colors.neutralGray
  const connectorVisible = resolved !== 'blocked'
  return (
    <Step>
      <StepIconContainer>
        <Icon color={statusColor} style={{ opacity: resolved === 'complete' ? 0.65 : 1 }} />
        {!isLast && (
          <StepContact
            color={connectorColor}
            $isVisible={connectorVisible}
            style={{ opacity: resolved === 'complete' ? 0.65 : 1 }}
          />
        )}
      </StepIconContainer>
      <StepContent>
        <StepTitle>{title}</StepTitle>
        <StepDescription>{description}</StepDescription>
      </StepContent>
    </Step>
  )
}
